/**
 * What a buyer sees about their own order's self-service options (plan
 * 2026-09-09-1309, U14 — R32, R33, R34; KD8, KD14).
 *
 * Carried on the two unauthenticated buyer payloads, the public order read and
 * the my-tickets row, so both can offer "Cancel registration", "Request a
 * refund" or neither without a second request. The endpoints that act on it
 * are in `api/ticketed-events/refund-requests.ts`.
 *
 * Lives with the entity schemas rather than the API contracts because the
 * order shapes in `ticketed-event-order.ts` embed it.
 */

import { z } from 'zod'

/**
 * The refund state a buyer sees for one order, folded from every refund row on
 * it (the buyer's own order-wide request and any per-ticket rows an admin's
 * cancel raised). `none` means no row exists.
 */
export const BuyerRefundStatusSchema = z.enum(['none', 'requested', 'owed', 'settled', 'declined'])
export type BuyerRefundStatus = z.infer<typeof BuyerRefundStatusSchema>

/**
 * Why a buyer cannot cancel this order themselves. `paid_order` points to the
 * refund request instead (KD8); `deadline_passed` comes with the school's
 * contact path (R34, AE9).
 */
export const BuyerCancelRefusalReasonSchema = z.enum([
  'already_cancelled',
  'not_confirmed',
  'event_cancelled',
  'event_past',
  'paid_order',
  'deadline_passed'
])
export type BuyerCancelRefusalReason = z.infer<typeof BuyerCancelRefusalReasonSchema>

export const BuyerCancellabilitySchema = z.object({
  allowed: z.boolean(),
  /** Null exactly when `allowed` is true. */
  reason: BuyerCancelRefusalReasonSchema.nullable(),
  /** The event's cancellation deadline; null means cancelling stays open until the event starts. */
  deadlineAt: z.string().nullable()
})
export type BuyerCancellability = z.infer<typeof BuyerCancellabilitySchema>

/**
 * Who a buyer asks when self-service is closed. `email` is the event's reply-to
 * address, the same one the public event page already shows; null when the
 * school set none.
 */
export const BuyerContactSchema = z.object({
  schoolName: z.string().nullable(),
  email: z.string().nullable()
})
export type BuyerContact = z.infer<typeof BuyerContactSchema>

/** Everything a buyer surface needs to offer cancel, refund request, or neither. */
export const BuyerSelfServiceSchema = z.object({
  cancellable: BuyerCancellabilitySchema,
  refundStatus: BuyerRefundStatusSchema,
  /** A confirmed paid order with no refund row yet. */
  refundRequestable: z.boolean(),
  contact: BuyerContactSchema
})
export type BuyerSelfService = z.infer<typeof BuyerSelfServiceSchema>
